import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { formatNumber } from '@vortx/shared-ui';
import { useThemeMode } from '@vortx/shared-ui';
import { vortxColors } from '@vortx/shared-ui';

export default function EmissionsPanel({ data }) {
  const { isDark } = useThemeMode();

  if (!data) return null;

  // Handle both direct data and nested outputs (NREL API wraps in outputs)
  const outputs = data.outputs || data;

  // Emissions can be at root level or under Site
  const site = outputs.Site || data.Site || {};
  const electricLoad = outputs.ElectricLoad || data.ElectricLoad || {};
  const electricUtility = outputs.ElectricUtility || data.ElectricUtility || {};

  // Debug: Log available emissions fields
  console.log('EmissionsPanel - Available data paths:', {
    'site keys': Object.keys(site),
    'site.annual_emissions_tonnes_CO2': site.annual_emissions_tonnes_CO2,
    'site.lifecycle_emissions_tonnes_CO2': site.lifecycle_emissions_tonnes_CO2,
    'electricUtility.annual_emissions_tonnes_CO2': electricUtility.annual_emissions_tonnes_CO2,
    'outputs keys': Object.keys(outputs),
  });

  // Support multiple field name formats:
  // 1. NREL web API format on Site: annual_emissions_tonnes_CO2, lifecycle_emissions_tonnes_CO2
  // 2. NREL BAU via ElectricUtility: emissions_factor_series_lb_CO2_per_kwh 
  // 3. Our Vortx Flux format: lifecycle_emissions_tonnes_CO2_bau on Site
  // 4. ElectricLoad may have emissions data for BAU
  let baselineValue =
    site.baseline_co2e_tons ??
    site.lifecycle_emissions_tonnes_CO2_bau ??
    site.annual_emissions_tonnes_CO2_bau ??
    outputs.lifecycle_emissions_tonnes_CO2_bau ??
    data.lifecycle_emissions_tonnes_CO2_bau ??
    electricLoad.annual_emissions_tonnes_CO2_bau ??
    // NREL API returns BAU values directly in Site
    site.annual_emissions_bau_tonnes_CO2 ??
    electricUtility.annual_emissions_tonnes_CO2_bau ??
    null;

  const projectValue =
    site.post_co2e_tons ??
    site.lifecycle_emissions_tonnes_CO2 ??
    site.annual_emissions_tonnes_CO2 ??
    outputs.lifecycle_emissions_tonnes_CO2 ??
    data.lifecycle_emissions_tonnes_CO2 ??
    // Standard NREL output
    site.annual_emissions_tonnes_CO2 ??
    null;

  // If we have project value but no baseline, try to estimate baseline
  // Using the lifecycle_emissions_reduction_CO2_fraction if available
  if (baselineValue === null && projectValue !== null) {
    const reductionFraction =
      site.lifecycle_emissions_reduction_CO2_fraction ??
      outputs.lifecycle_emissions_reduction_CO2_fraction ??
      data.lifecycle_emissions_reduction_CO2_fraction;

    if (reductionFraction !== null && reductionFraction !== undefined && reductionFraction < 1) {
      // If reduction = 30%, then project = baseline * (1 - 0.30), so baseline = project / (1 - reduction)
      baselineValue = projectValue / (1 - reductionFraction);
    }
  }

  // If no emissions data is available, don't render
  if (baselineValue === null && projectValue === null) return null;

  const chartData = [
    { name: 'Baseline', value: baselineValue ?? 0 },
    { name: 'Project', value: projectValue ?? 0 },
  ];

  const reduction =
    baselineValue != null && projectValue != null
      ? baselineValue - projectValue
      : null;

  const reductionPercent =
    baselineValue != null && projectValue != null && baselineValue > 0
      ? ((baselineValue - projectValue) / baselineValue) * 100
      : null;

  // Theme-aware colors
  const textColor = isDark ? '#ffffff' : vortxColors.charcoal;
  const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const tooltipBg = isDark ? '#1a1a1a' : '#ffffff';
  const tooltipBorder = isDark ? '#444' : '#ccc';

  // Bar colors: gray for baseline, gold for project (improved)
  const barColors = [isDark ? vortxColors.stone : vortxColors.charcoal, vortxColors.gold];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Emissions</Typography>
      <Box sx={{ width: '100%', height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <XAxis
              dataKey="name"
              tick={{ fill: textColor }}
              axisLine={{ stroke: gridColor }}
            />
            <YAxis
              label={{ value: 'tCO₂e', angle: -90, position: 'insideLeft', fill: textColor }}
              tick={{ fill: textColor }}
              axisLine={{ stroke: gridColor }}
            />
            <Tooltip
              formatter={(v) => formatNumber(v, 0)}
              contentStyle={{
                backgroundColor: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                color: textColor
              }}
            />
            <Bar dataKey="value">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={barColors[index]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Emissions summary */}
      <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
        {reduction != null && (
          <Typography variant="body2">
            Reduction: <b style={{ color: vortxColors.gold }}>{formatNumber(reduction, 0)} tCO₂e</b>
          </Typography>
        )}
        {reductionPercent != null && (
          <Typography variant="body2">
            <b style={{ color: vortxColors.gold }}>{reductionPercent.toFixed(1)}%</b> decrease
          </Typography>
        )}
      </Box>

      {/* Show if baseline was estimated */}
      {baselineValue === null && projectValue !== null && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Baseline not available from solver
        </Typography>
      )}
    </Box>
  );
}
